import { utils } from "@ricky0123/vad-web"
import { useEffect, useRef, useState } from "react"
import {
  AudioSource,
  MAX_SESSION_MS,
  Preferred,
  Region,
  VadParams,
} from "../shared/protocol"
import { normalize } from "../shared/regions"
import { drawArms } from "../shared/sampler"
import { Annotator } from "./annotate"
import { submit } from "./db"
import { ComparisonDriver, decodeAudioFile } from "./driver"
import { drawOrb, drawTrace } from "./graph"
import themes from "./theme.json"

type Phase =
  | "loading"
  | "ready"
  | "recording"
  | "processing"
  // The two review steps are deliberately separate. Marking happens with
  // both alternatives off screen so the annotation is not drawn toward
  // either of them; only then does the comparison appear.
  | "annotate"
  | "choose"
  | "submitting"
  | "submitted"
  | "error"

interface UiClip {
  key: number
  startMs: number
  endMs: number
  url: string
}

/** clips[i] belongs to arms[i]. */
const emptyClips = (): UiClip[][] => [[], []]

let clipKey = 0

export function App() {
  const [phase, setPhase] = useState<Phase>("loading")
  // What the sampler proposed. Nothing is recorded when this is drawn — a
  // pair that gets abandoned leaves no row — so it lives only in state until
  // a comparison is submitted.
  const [drawnArms, setDrawnArms] = useState<VadParams[]>([])
  // The parameters actually running, initialized from the drawn pair and
  // diverging from it when the user edits.
  const [armsUsed, setArmsUsed] = useState<VadParams[]>([])
  const [clips, setClips] = useState<UiClip[][]>(emptyClips())
  const [regions, setRegions] = useState<Region[]>([])
  const [editingArm, setEditingArm] = useState<number | null>(null)
  const [source, setSource] = useState<AudioSource>("mic")
  const [fileName, setFileName] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string>("")
  const [notice, setNotice] = useState<string>("")

  const driverRef = useRef<ComparisonDriver | null>(null)
  const graphRef = useRef<HTMLCanvasElement | null>(null)
  const orbRef = useRef<HTMLDivElement | null>(null)
  const haloRef = useRef<HTMLDivElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const fileAudioRef = useRef<Float32Array | null>(null)
  const sessionAudioRef = useRef<Float32Array>(new Float32Array(0))
  const armsUsedRef = useRef<VadParams[]>([])
  armsUsedRef.current = armsUsed

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const driver = await ComparisonDriver.create()
        if (cancelled) return
        // Drawing is throttled because file processing runs far faster than
        // real time and repainting per frame would dominate it.
        let lastDraw = 0
        driver.onFrame = (prob) => {
          const now = performance.now()
          if (now - lastDraw < 33) return
          lastDraw = now
          drawOrb(orbRef.current, haloRef.current, prob)
          drawTrace(graphRef.current, driver.trace, armsUsedRef.current)
        }
        driver.onClip = ({ armIndex, audio, startMs, endMs }) => {
          const wav = new Blob([utils.encodeWAV(audio)], { type: "audio/wav" })
          const clip: UiClip = {
            key: clipKey++,
            startMs,
            endMs,
            url: URL.createObjectURL(wav),
          }
          setClips((prev) =>
            prev.map((list, i) => (i === armIndex ? [...list, clip] : list))
          )
        }
        driver.onLimit = () => {
          setNotice(
            `Stopped at ${MAX_SESSION_MS / 60000} minutes — that is as much as one session holds.`
          )
          void finishRecording()
        }
        driverRef.current = driver
        newComparison()
        if (!cancelled) setPhase("ready")
      } catch (e) {
        console.error(e)
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e))
          setPhase("error")
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  /** Rebinds the driver to `params` and clears everything recorded so far. */
  function resetRecording(params: VadParams[]) {
    driverRef.current!.setArms(params)
    setClips((prev) => {
      for (const clip of prev.flat()) URL.revokeObjectURL(clip.url)
      return emptyClips()
    })
    setRegions([])
    sessionAudioRef.current = new Float32Array(0)
    const canvas = graphRef.current
    canvas?.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height)
    drawOrb(orbRef.current, haloRef.current, 0)
  }

  /** Draws a fresh pair. Nothing is sent until a comparison is submitted. */
  function newComparison() {
    const arms = drawArms()
    setDrawnArms(arms)
    setArmsUsed(arms)
    armsUsedRef.current = arms
    resetRecording(arms)
  }

  function refresh() {
    setNotice("")
    newComparison()
    setPhase("ready")
  }

  async function start() {
    try {
      setNotice("")
      setSource("mic")
      await driverRef.current!.start()
      setPhase("recording")
    } catch {
      setError(
        "Microphone access failed. The page needs the mic to run the comparison — nothing is uploaded."
      )
      setPhase("error")
    }
  }

  async function finishRecording() {
    const driver = driverRef.current!
    await driver.stop()
    sessionAudioRef.current = driver.sessionAudio()
    drawOrb(orbRef.current, haloRef.current, 0)
    setPhase("annotate")
  }

  async function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return
    try {
      const audio = await decodeAudioFile(file)
      const driver = driverRef.current!
      if (audio.length / driver.frameSamples > driver.maxSessionFrames) {
        setError(
          `That file is longer than ${MAX_SESSION_MS / 60000} minutes, which is as much as one session holds. Try a shorter excerpt.`
        )
        setPhase("error")
        return
      }
      fileAudioRef.current = audio
      setFileName(file.name)
      await runFile(audio)
    } catch (err) {
      console.error(err)
      setError(
        "Could not decode that file. It never left your browser — try a common format like wav, mp3 or ogg."
      )
      setPhase("error")
    }
  }

  async function runFile(audio: Float32Array) {
    const driver = driverRef.current!
    resetRecording(armsUsedRef.current)
    setSource("file")
    setProgress(0)
    setPhase("processing")
    await driver.processFile(audio, setProgress)
    sessionAudioRef.current = driver.sessionAudio()
    drawTrace(graphRef.current, driver.trace, armsUsedRef.current)
    drawOrb(orbRef.current, haloRef.current, 0)
    setPhase("annotate")
  }

  function applyEdit(index: number, params: VadParams) {
    const next = armsUsed.map((p, i) => (i === index ? params : p))
    setArmsUsed(next)
    armsUsedRef.current = next
    setEditingArm(null)
    // The clips on screen were produced by the old parameters, so they no
    // longer describe what would run; start the comparison over.
    resetRecording(next)
    setPhase("ready")
  }

  async function choose(preferred: Preferred) {
    const driver = driverRef.current!
    setPhase("submitting")
    try {
      await submit({
        preferred,
        recording_duration_ms: Math.round(driver.recordingDurationMs),
        source,
        drawn_arms: drawnArms,
        arms: armsUsed,
        trace: driver.trace,
        ideal_regions: normalize(regions),
        clips: clips.map((list) =>
          list.map(({ startMs, endMs }) => ({ startMs, endMs }))
        ),
      })
      setPhase("submitted")
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setPhase("error")
    }
  }

  if (phase === "error") {
    return (
      <Shell>
        <div role="alert" className="alert alert-error alert-soft">
          <div>
            <p className="font-medium">Something went wrong</p>
            <p className="mt-1 text-sm">{error}</p>
            <button
              className="btn btn-sm btn-outline mt-3"
              onClick={() => location.reload()}
            >
              Reload
            </button>
          </div>
        </div>
      </Shell>
    )
  }

  const busy =
    phase === "recording" || phase === "processing" || phase === "submitting"
  const driver = driverRef.current
  // The alternatives stay hidden through the marking step; see Phase above.
  const showArms = phase !== "annotate" && armsUsed.length > 0

  return (
    <Shell>
      <section className="card mb-8 border border-base-300 bg-base-200">
        {/* Stacks vertically on small screens; the side-by-side arrangement
            only appears once there is room for it. */}
        <div className="card-body flex-col items-stretch gap-5 p-5 md:flex-row md:items-center md:gap-6">
          <div className="relative flex h-24 w-24 shrink-0 items-center justify-center self-center">
            {/* base-content rather than primary: the black theme's primary is
                a dark gray that vanishes against the card. */}
            <div
              ref={haloRef}
              className="orb absolute h-20 w-20 rounded-full bg-base-content opacity-0"
            />
            <div
              ref={orbRef}
              className="orb relative h-12 w-12 rounded-full bg-base-content"
            />
          </div>

          <div className="w-full min-w-0 flex-1">
            {/* The trace line is a fixed light color, so the canvas stays
                black in both themes rather than following a surface token. */}
            <canvas
              ref={graphRef}
              className="h-24 w-full rounded-lg border border-base-300 bg-black"
              height={96}
            />
            <div className="mt-1 flex justify-between text-sm opacity-60">
              <span>speech probability</span>
              <span>{statusLabel(phase, progress)}</span>
            </div>
          </div>

          <div className="flex w-full flex-row flex-wrap gap-2 md:w-auto md:shrink-0 md:flex-col">
            {phase === "recording" ? (
              <button
                onClick={finishRecording}
                className="btn btn-error flex-1 px-6 md:flex-none"
              >
                Stop
              </button>
            ) : (
              <button
                onClick={start}
                disabled={phase !== "ready"}
                className="btn btn-primary flex-1 px-6 md:flex-none"
              >
                Start
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={onFilePicked}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={phase !== "ready"}
              className="btn btn-outline btn-sm flex-1 md:flex-none"
              title="Run the comparison on an audio file instead of the mic. The file is processed in your browser and never uploaded."
            >
              Test a file
            </button>
            {fileName && phase === "ready" && (
              <button
                onClick={() => runFile(fileAudioRef.current!)}
                className="btn btn-outline btn-sm flex-1 md:flex-none"
                title={`Run the current parameters on ${fileName}`}
              >
                Re-run {truncate(fileName, 14)}
              </button>
            )}
            <button
              onClick={refresh}
              disabled={busy || phase === "loading"}
              className="btn btn-outline btn-sm flex-1 md:flex-none"
              title="Get a different pair of parameter sets"
            >
              New parameters
            </button>
          </div>
        </div>
      </section>

      {notice && (
        <div role="status" className="alert alert-info alert-soft mb-8">
          <span className="text-sm">{notice}</span>
        </div>
      )}

      {phase === "annotate" && driver && (
        <section className="card mb-8 border border-base-300 bg-base-200">
          <div className="card-body p-5">
            <h2 className="text-lg font-semibold">
              Where should the speech have been captured?
            </h2>
            <p className="max-w-3xl text-sm opacity-70">
              Both alternatives are hidden for this step on purpose. Mark what
              you would have wanted captured from this recording, not what
              either side happened to produce — everything the comparison is
              scored on is measured against these regions.
            </p>
            <div className="mt-3">
              <Annotator
                trace={driver.trace}
                msPerFrame={driver.msPerFrame}
                durationMs={driver.recordingDurationMs}
                audio={sessionAudioRef.current}
                regions={regions}
                onChange={setRegions}
              />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                className="btn btn-primary"
                disabled={regions.length === 0}
                onClick={() => setPhase("choose")}
              >
                Done marking
              </button>
              {regions.length === 0 && (
                <span className="text-sm opacity-60">
                  Mark at least one region to continue.
                </span>
              )}
            </div>
          </div>
        </section>
      )}

      {showArms && (
        <section className="grid gap-5 md:grid-cols-2">
          {armsUsed.map((params, i) => (
            <ArmCard
              key={i}
              index={i}
              params={params}
              edited={JSON.stringify(params) !== JSON.stringify(drawnArms[i])}
              clips={clips[i] ?? []}
              onEdit={() => setEditingArm(i)}
              editDisabled={busy || phase === "loading"}
            />
          ))}
        </section>
      )}

      {phase === "choose" && (
        <section className="card mt-8 border border-base-300 bg-base-200">
          <div className="card-body p-5">
            <h2 className="text-lg font-semibold">
              Which alternative did better overall?
            </h2>
            <p className="text-sm opacity-60">
              Judge the whole session rather than any single clip.
            </p>
            <div className="mt-2 flex flex-wrap gap-3">
              {armsUsed.map((_, i) => (
                <button
                  key={i}
                  onClick={() => choose(i as Preferred)}
                  className="btn btn-outline"
                >
                  Alternative {i + 1}
                </button>
              ))}
              <button
                onClick={() => setPhase("annotate")}
                className="btn btn-ghost btn-sm self-center"
              >
                Back to marking
              </button>
            </div>
          </div>
        </section>
      )}

      {phase === "submitted" && (
        <section className="card mt-8 border border-success/30 bg-base-200">
          <div className="card-body p-5">
            <p className="text-base font-medium text-success">
              Thanks — your comparison was recorded.
            </p>
            <div>
              <button onClick={refresh} className="btn btn-success btn-sm mt-2">
                Try another comparison
              </button>
            </div>
          </div>
        </section>
      )}

      {editingArm !== null && (
        <ParamEditor
          title={`Alternative ${editingArm + 1} parameters`}
          initial={armsUsed[editingArm]!}
          onCancel={() => setEditingArm(null)}
          onSave={(params) => applyEdit(editingArm, params)}
        />
      )}
    </Shell>
  )
}

function statusLabel(phase: Phase, progress: number): string {
  switch (phase) {
    case "loading":
      return "loading model…"
    case "ready":
      return "ready"
    case "recording":
      return "listening"
    case "processing":
      return `processing file… ${Math.round(progress * 100)}%`
    case "annotate":
      return "stopped — mark the ideal regions"
    case "choose":
      return "stopped — pick a side"
    case "submitting":
      return "sending…"
    case "submitted":
      return "submitted"
    default:
      return ""
  }
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + "…"
}

const PARAM_FIELDS: {
  key: keyof VadParams
  label: string
  step: number
}[] = [
  { key: "positiveSpeechThreshold", label: "Positive speech threshold", step: 0.01 },
  { key: "negativeSpeechThreshold", label: "Negative speech threshold", step: 0.01 },
  { key: "redemptionMs", label: "Redemption (ms)", step: 50 },
  { key: "preSpeechPadMs", label: "Pre-speech pad (ms)", step: 50 },
  { key: "minSpeechMs", label: "Min speech (ms)", step: 50 },
]

// Mirrors the library's validateOptions and the database's is_vad_params, so
// a value the modal accepts is also one the insert will accept.
function paramError(v: VadParams): string | null {
  if (!(v.positiveSpeechThreshold > 0 && v.positiveSpeechThreshold <= 1)) {
    return "The positive threshold must be between 0 (exclusive) and 1."
  }
  if (
    !(
      v.negativeSpeechThreshold >= 0 &&
      v.negativeSpeechThreshold <= v.positiveSpeechThreshold
    )
  ) {
    return "The negative threshold must be between 0 and the positive threshold."
  }
  for (const key of ["redemptionMs", "preSpeechPadMs", "minSpeechMs"] as const) {
    if (!(v[key] >= 0 && v[key] <= 600_000)) {
      return "Durations must be between 0 and 600000 ms."
    }
  }
  return null
}

function ParamEditor({
  title,
  initial,
  onCancel,
  onSave,
}: {
  title: string
  initial: VadParams
  onCancel: () => void
  onSave: (params: VadParams) => void
}) {
  const [values, setValues] = useState<Record<keyof VadParams, string>>(() => {
    const out = {} as Record<keyof VadParams, string>
    for (const f of PARAM_FIELDS) out[f.key] = String(initial[f.key])
    return out
  })

  const parsed = {} as VadParams
  let parseFailed = false
  for (const f of PARAM_FIELDS) {
    const n = Number(values[f.key])
    if (values[f.key].trim() === "" || !Number.isFinite(n)) parseFailed = true
    parsed[f.key] = n
  }
  const error = parseFailed ? "Every field needs a number." : paramError(parsed)

  return (
    <div className="modal modal-open" role="dialog">
      <div className="modal-box max-w-sm">
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="mt-1 text-sm opacity-60">
          Saving clears the current recording, since its clips were produced by
          the old values.
        </p>
        <div className="mt-4 space-y-3">
          {PARAM_FIELDS.map((f) => (
            <label key={f.key} className="flex items-center justify-between gap-3">
              <span className="text-sm">{f.label}</span>
              <input
                type="number"
                step={f.step}
                value={values[f.key]}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, [f.key]: e.target.value }))
                }
                className="input input-bordered input-sm w-32 text-right font-mono"
              />
            </label>
          ))}
        </div>
        {error && <p className="mt-3 text-sm text-error">{error}</p>}
        <div className="modal-action">
          <button className="btn btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={error !== null}
            onClick={() => onSave(parsed)}
          >
            Save
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onCancel} />
    </div>
  )
}

// The native <audio controls> element has a large minimum width that
// overflows narrow screens, so clips get a plain play/pause button instead.
// Module-level so starting one clip stops whichever other clip was playing.
let activeAudio: HTMLAudioElement | null = null

function ClipPlayer({ url }: { url: string }) {
  const [playing, setPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    return () => {
      audioRef.current?.pause()
      if (activeAudio === audioRef.current) activeAudio = null
    }
  }, [])

  function toggle() {
    if (!audioRef.current) {
      const audio = new Audio(url)
      audio.onended = () => setPlaying(false)
      audio.onpause = () => setPlaying(false)
      audioRef.current = audio
    }
    const audio = audioRef.current
    if (playing) {
      audio.pause()
      audio.currentTime = 0
      return
    }
    if (activeAudio && activeAudio !== audio) activeAudio.pause()
    activeAudio = audio
    audio.currentTime = 0
    void audio.play()
    setPlaying(true)
  }

  return (
    <button
      onClick={toggle}
      className="btn btn-circle btn-sm shrink-0"
      title={playing ? "Stop playback" : "Play clip"}
    >
      {playing ? (
        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
          <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
          <path d="M8 5v14l11-7z" />
        </svg>
      )}
    </button>
  )
}

function ThemeToggle() {
  const [theme, setThemeState] = useState(
    () => document.documentElement.getAttribute("data-theme") ?? themes.light
  )

  function setTheme(next: string) {
    setThemeState(next)
    document.documentElement.setAttribute("data-theme", next)
    localStorage.setItem("theme", next)
  }

  const dark = theme === themes.dark
  return (
    <label className="btn btn-ghost btn-sm swap swap-rotate" title="Toggle theme">
      <input
        type="checkbox"
        checked={dark}
        onChange={() => setTheme(dark ? themes.light : themes.dark)}
      />
      <svg
        className="swap-off h-5 w-5 fill-current"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
      >
        <path d="M5.64,17l-.71.71a1,1,0,0,0,0,1.41,1,1,0,0,0,1.41,0l.71-.71A1,1,0,0,0,5.64,17ZM5,12a1,1,0,0,0-1-1H3a1,1,0,0,0,0,2H4A1,1,0,0,0,5,12Zm7-7a1,1,0,0,0,1-1V3a1,1,0,0,0-2,0V4A1,1,0,0,0,12,5ZM5.64,7.05a1,1,0,0,0,.7.29,1,1,0,0,0,.71-.29,1,1,0,0,0,0-1.41l-.71-.71A1,1,0,0,0,4.93,6.34Zm12,.29a1,1,0,0,0,.7-.29l.71-.71a1,1,0,1,0-1.41-1.41L17,5.64a1,1,0,0,0,0,1.41A1,1,0,0,0,17.66,7.34ZM21,11H20a1,1,0,0,0,0,2h1a1,1,0,0,0,0-2Zm-9,8a1,1,0,0,0-1,1v1a1,1,0,0,0,2,0V20A1,1,0,0,0,12,19ZM18.36,17A1,1,0,0,0,17,18.36l.71.71a1,1,0,0,0,1.41,0,1,1,0,0,0,0-1.41ZM12,6.5A5.5,5.5,0,1,0,17.5,12,5.51,5.51,0,0,0,12,6.5Zm0,9A3.5,3.5,0,1,1,15.5,12,3.5,3.5,0,0,1,12,15.5Z" />
      </svg>
      <svg
        className="swap-on h-5 w-5 fill-current"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
      >
        <path d="M21.64,13a1,1,0,0,0-1.05-.14,8.05,8.05,0,0,1-3.37.73A8.15,8.15,0,0,1,9.08,5.49a8.59,8.59,0,0,1,.25-2A1,1,0,0,0,8,2.36,10.14,10.14,0,1,0,22,14.05,1,1,0,0,0,21.64,13Zm-9.5,6.69A8.14,8.14,0,0,1,7.08,5.22v.27A10.15,10.15,0,0,0,17.22,15.63a9.79,9.79,0,0,0,2.1-.22A8.11,8.11,0,0,1,12.14,19.73Z" />
      </svg>
    </label>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto max-w-5xl px-6 pb-10 pt-4">
      <div className="mb-6 flex items-center justify-end gap-1">
        <a
          href="https://github.com/ricky0123/vad"
          target="_blank"
          rel="noreferrer"
          className="btn btn-ghost btn-sm"
          aria-label="GitHub repository"
          title="github.com/ricky0123/vad"
        >
          <svg viewBox="0 0 16 16" className="h-5 w-5 fill-current">
            <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27s1.36.09 2 .27c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
          </svg>
        </a>
        <ThemeToggle />
      </div>
      <header className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight">
          Which one felt better?
        </h1>
        <p className="mt-3 max-w-2xl text-base leading-relaxed opacity-80">
          Talk for as long as you like — say whatever you want — or run an audio
          file through instead. Two different settings listen to the same audio
          at the same time, and each side shows the clips its settings produced.
          Stop when you have a feel for it, mark where the speech should have
          been captured, then say which side did better.
        </p>
        <p className="mt-2 text-base opacity-60">
          Your audio never leaves this page — microphone or file. What gets
          uploaded is the per-frame speech probabilities, the clip boundaries,
          the regions you mark, the parameters, and your answer.
        </p>
      </header>
      {children}
    </main>
  )
}

function ArmCard({
  index,
  params,
  edited,
  clips,
  onEdit,
  editDisabled,
}: {
  index: number
  params: VadParams
  edited: boolean
  clips: UiClip[]
  onEdit: () => void
  editDisabled: boolean
}) {
  return (
    <div className="card border border-base-300 bg-base-200">
      <div className="card-body p-5">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold">
            VAD Parameters — Alternative {index + 1}
            {edited && (
              <span className="badge badge-outline ml-2 align-middle">edited</span>
            )}
          </h2>
          <span className="whitespace-nowrap text-sm opacity-50">
            {clips.length} clip{clips.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="font-mono text-xs leading-relaxed opacity-60">
            threshold {params.positiveSpeechThreshold} /{" "}
            {params.negativeSpeechThreshold} · redemption {params.redemptionMs}
            ms · min speech {params.minSpeechMs}ms
          </p>
          <button
            className="btn btn-ghost btn-xs"
            onClick={onEdit}
            disabled={editDisabled}
          >
            Edit
          </button>
        </div>
        <div className="mt-2 flex-1 space-y-3">
          {clips.length === 0 && (
            <p className="text-base opacity-50">No clips yet.</p>
          )}
          {clips.map((clip, i) => (
            <div key={clip.key} className="rounded-lg border border-base-300 p-3">
              <div className="flex items-center gap-3">
                <ClipPlayer url={clip.url} />
                <span className="font-mono text-sm opacity-50">#{i + 1}</span>
                <span className="ml-auto font-mono text-sm opacity-50">
                  {((clip.endMs - clip.startMs) / 1000).toFixed(1)}s
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
